import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from "@tanstack/react-query";
import SearchLookup from './SearchLookup/SearchLookup';
import { getServices } from '../../api/serviceApi';
import { CURRENCY_CODE_LIST } from '../../config/constants';
import { useUserPreferences } from '../../hooks/useUserPreferences';

const ServiceLookupNative = ({
  label,
  onSelect,
  isError,
  maxDisplayLength = 50,
  disabled = false,
  required = false,
  defaultValue = null,
  selectedServices = [],
}) => {
  const [allServices, setAllServices] = useState([]);
  const { currency: userCurrency } = useUserPreferences();

  // Fetch services using React Query
  const { data: services, isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: getServices,
  });

  // Transform and filter services when data changes
  useEffect(() => {
    if (services) {
      const transformed = services
        .filter((service) => service.isActive)
        .map((service) => ({
          id: service._id,
          name: service.name,
          price: service.price,
          duration: service.duration,
          secondary: `${
            CURRENCY_CODE_LIST.find((code) => code.id === userCurrency)
              ?.symbol || "$"
          }${service.price} · ${service.duration} min`,
        }));

      setAllServices(transformed);
    } else {
      setAllServices([]);
    }
  }, [services, userCurrency]);

  // Custom search function for local filtering
  const searchServices = useCallback(async (query) => {
    // If loading, return empty array
    if (isLoading) {
      return [];
    }

    // Filter available services (exclude already selected ones)
    const availableServices = allServices.filter(
      (service) => !selectedServices.includes(service.id)
    );

    // If no query, return all available services
    if (!query.trim()) {
      return availableServices;
    }

    // Filter by query (case-insensitive name search)
    const filtered = availableServices.filter(service =>
      service.name.toLowerCase().includes(query.toLowerCase())
    );

    return filtered;
  }, [allServices, selectedServices, isLoading]);

  return (
    <SearchLookup
      label={label}
      placeholder="Select services..."
      searchFunction={searchServices}
      onSelect={onSelect}
      isError={isError}
      maxDisplayLength={maxDisplayLength}
      disabled={disabled}
      required={required}
      defaultValue={defaultValue}
      debounceMs={200} // Faster debounce for local filtering
      showChevron={true} // Enable chevron functionality for better service discovery
      allowEmptyQuery={true} // Allow showing all services when no query
      clearOnSelect={true} // Clear input after selection for multi-service workflow
    />
  );
};

export default ServiceLookupNative;